import { dismissAlert, showError, showHttpError, showSuccess } from '@calendly/alert'
import { FormError } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { transform } from 'lodash-es'

import { moveToChangeSubscription } from 'actions/billing/subscription_modal'
import { setFormErrors } from 'actions/form'
import changeSet from 'core/change_set'
import { fetchStatistics } from 'features/organization_statistics'
import { createGroup, listGroups } from 'features/user_groups'
import { rootApi } from 'root-api'
import { isSingleSelector } from 'selectors/current_organization'
import { isAdminOrOwnerOrGroupAdminSelector, isOnlyGroupAdminSelector } from 'selectors/current_user'

import { createInvitations, getManagedEventTypes, validateInvitations } from './api'
import {
  managedEventTypesPaginationSelector,
  managedEventTypesSortOrderSelector,
  recaptchaCallbackSelector,
  recaptchaV2TokenSelector,
  selectedGroupIdSelector
} from './selectors'
import { STEPS } from './steps'
import { translateBackendErrorsToForm } from './utils'

const nextStep = createAction('inviteFlow/nextStep')
const moveToStep = createAction('inviteFlow/moveToStep')
const resetFormInvitations = createAction('inviteFlow/resetFormInvitations')
const saveBackendErrors = createAction('inviteFlow/saveBackendErrors')
const previousStep = createAction('inviteFlow/previousStep')
const startSubmission = createAction('inviteFlow/startSubmission')
const finishSubmission = createAction('inviteFlow/finishSubmission')

export const selectUserGroup = createAction('inviteFlow/userGroups/select')

const ERROR_TYPES = {
  validation: 'bulk_validation',
  quantity: 'quantity_overflow',
  trial_quantity: 'trial_quantity_overflow',
  cant_assign_user_group: 'cant_assign_user_group'
}

const isSubscriptionQuantityError = (response) => (
  response?.type === ERROR_TYPES.quantity
  || response?.type === ERROR_TYPES.trial_quantity
  || response?.type === ERROR_TYPES.cant_assign_user_group
)

const showSubscriptionQuantityError = ({ dispatch, onSuccess, response, stepId }) => {
  if (stepId >= 0) { dispatch(moveToStep(stepId)) }

  const {
    invitations_quantity: invitationsQuantity,
    min_quantity: minQuantity
  } = response.payload || {}

  const featureKey = response.type === ERROR_TYPES.cant_assign_user_group
    ? 'user_groups'
    : 'organization_invitation'

  dispatch(moveToChangeSubscription({
    featureKey,
    minQuantity,
    invitationsQuantity,
    onSuccess
  }))
}

const setFormBulkErrors = (dispatch, response) => {
  const { payload, type } = response
  switch (type) {
    case ERROR_TYPES.validation: {
      const error = new FormError()
      error.payload = translateBackendErrorsToForm(payload)
      throw error
    }
    case ERROR_TYPES.cant_assign_user_group:
    case ERROR_TYPES.quantity:
    case ERROR_TYPES.trial_quantity: {
      return false
    }
    default:
      return dispatch(showHttpError(response))
  }
}

const withSubmissionLock = async ({ callback, dispatch, getState }) => {
  if (getState().inviteFlow?.isSubmitting) return false

  dispatch(startSubmission())
  try {
    return await callback()
  } finally {
    dispatch(finishSubmission())
  }
}

const validate = createAsyncThunk(
  'inviteFlow/validate',
  async ({ data, stepId }, { rejectWithValue, dispatch }) => {
    dispatch(dismissAlert())

    const [success, response] = await validateInvitations(data)

    if (!success) {
      switch (response.type) {
        case ERROR_TYPES.validation: {
          // On validation error render list of errors in field notification
          if (stepId >= 0) { dispatch(moveToStep(stepId)) }
          return rejectWithValue(response)
        }
        case ERROR_TYPES.cant_assign_user_group:
        case ERROR_TYPES.quantity:
        case ERROR_TYPES.trial_quantity: {
          // On quantity overflow render update seats popup
          showSubscriptionQuantityError({
            dispatch,
            onSuccess: () => dispatch(validate({ data, stepId })),
            response,
            stepId
          })

          return rejectWithValue(response)
        }
        default: {
          dispatch(showHttpError(response))
          return rejectWithValue(response)
        }
      }
    }

    return data
  }
)

export const validateAndGoNext = (data) => async (dispatch, getState) => withSubmissionLock({
  dispatch,
  getState,
  callback: async () => {
    if (!data.invitations.length) {
      dispatch(showError(I18n.t('publisher.organization.invite_flow.add_emails_step.empty_list')))
      return false
    }

    const { payload, error } = await dispatch(validate({ data, nextStep: true }))

    if (error) {
      await dispatch(saveBackendErrors(payload))
      setFormBulkErrors(dispatch, payload)
      return false
    }

    changeSet.dirty('new-invitations-wizard')
    dispatch(nextStep())
    return true
  }
})

export const validateAndGoBack = (data) => async (dispatch, getState) => withSubmissionLock({
  dispatch,
  getState,
  callback: async () => {
    if (!data.invitations.length) {
      dispatch(showError(I18n.t('publisher.organization.invite_flow.add_emails_step.empty_list')))
      return false
    }

    const { payload, error } = await dispatch(validate({ data }))

    if (error) {
      await dispatch(saveBackendErrors(payload))
      setFormBulkErrors(dispatch, payload)
      return false
    }

    dispatch(previousStep())
    return true
  }
})

export const fetchUserGroups = createAsyncThunk(
  'inviteFlow/userGroups/fetch',
  async (query, { dispatch, rejectWithValue }) => {
    const trimmed = typeof query === 'string' ? query.trim() : ''
    const params = { page_size: 20 }
    if (trimmed) {
      params.filter_term = trimmed
    }

    const [success, response] = await listGroups(params)

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue([])
    }

    return response
  }
)

const createUserGroup = createAsyncThunk(
  'inviteFlow/userGroups/create',
  async (params, { _, rejectWithValue }) => {
    const [success, response] = await createGroup(params)

    if (!success) {
      return rejectWithValue(response)
    }

    return response
  }
)

export const createAndSelectUserGroup = (params) => async (dispatch) => {
  const { payload, error } = await dispatch(createUserGroup(params))
  if (error) {
    dispatch(setFormErrors(payload))
  } else {
    dispatch(selectUserGroup(payload.id))
  }
}

export const fetchManagedEventTypes = createAsyncThunk(
  'inviteFlow/managedEventTypes/fetch',
  async (_, { rejectWithValue, dispatch, getState }) => {
    const state = getState()
    const sortOrder = managedEventTypesSortOrderSelector(state)
    const { currentPage, pageSize } = managedEventTypesPaginationSelector(state)
    const selectedGroupId = selectedGroupIdSelector(state)
    const [success, response] = await getManagedEventTypes(
      {
        sortOrder,
        page: currentPage,
        pageSize,
        groupId: selectedGroupId,
        onlyOrgOwned: !selectedGroupId
      }
    )

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(null)
    }

    return response
  }
)

const initialState = {
  currentStepIndex: 0,
  steps: [],
  isSubmitting: false,
  isNextStepLoading: false,
  isPreviousStepLoading: false,
  errors: {},
  form: {
    eventTypeIds: [],
    invitations: [],
    groupId: null
  },
  userGroups: {
    list: [],
    isLoading: false,
    selected: null,
    anyGroups: null
  },
  managedEventTypes: {
    list: [],
    isLoading: false,
    selectedIds: [],
    sortOrder: 'asc',
    pagination: {
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
    },
  },
  modal: {
    success: false,
    open: false
  },
  mfa: {
    error: null,
    grantFingerprint: null,
    open: false,
    verificationGrant: null
  },
  recaptcha: {
    callback: () => Promise.resolve(null),
    challengeKey: 0,
    showChallenge: false,
    token: null,
    v2Token: null
  },
  role: 'user'
}

const slice = createSlice({
  name: 'inviteFlow',
  initialState,
  reducers: {
    resetState: () => initialState,
    setSteps(state, { payload }) {
      state.steps = payload
    },
    toggleSort(state) {
      state.managedEventTypes.sortOrder = state.managedEventTypes.sortOrder === 'asc' ? 'desc' : 'asc'
    },
    setPage(state, { payload: page }) {
      state.managedEventTypes.pagination.currentPage = page
    },
    setAnyGroups(state, { payload: anyGroups }) {
      state.userGroups.anyGroups = anyGroups
    },
    setRecaptchaCallback(state, { payload }) {
      state.recaptcha.callback = payload
    },
    showRecaptchaChallenge(state) {
      state.recaptcha.showChallenge = true
      state.recaptcha.v2Token = null
      state.recaptcha.challengeKey += 1
    },
    setRecaptchaV2Token(state, { payload }) {
      state.recaptcha.v2Token = payload
    },
    clearRecaptchaV2Token(state) {
      state.recaptcha.v2Token = null
    },
    refreshRecaptchaChallenge(state) {
      state.recaptcha.showChallenge = true
      state.recaptcha.v2Token = null
      state.recaptcha.challengeKey += 1
    },
    resetRecaptchaChallenge(state) {
      state.recaptcha.showChallenge = false
      state.recaptcha.v2Token = null
    },
    resetVerificationState(state) {
      state.mfa = { ...initialState.mfa }
      state.recaptcha = {
        ...initialState.recaptcha,
        challengeKey: state.recaptcha.challengeKey + 1
      }
    },
    removeBackendError(state, { payload }) {
      state.errors = transform(state.errors, (result, value, key) => {
        if (payload !== key) {
          result[key] = value
        }

        return result
      }, {})
    },
    setRole(state, { payload }) {
      state.role = payload
    },
    setModalSuccessState(state, { payload }) {
      state.modal.success = payload
    },
    openAddUserModal(state) {
      state.modal.open = true
    },
    closeAddUserModal(state) {
      state.modal.open = false
      state.modal.success = false
      state.mfa.error = null
      state.mfa.grantFingerprint = null
      state.mfa.open = false
      state.mfa.verificationGrant = null
      state.recaptcha.showChallenge = false
      state.recaptcha.v2Token = null
    },
    openMfa(state) {
      state.mfa.error = null
      state.mfa.open = true
    },
    closeMfa(state) {
      state.mfa.error = null
      state.mfa.open = false
    },
    setMfaError(state, { payload }) {
      state.mfa.error = payload
    },
    clearMfaError(state) {
      state.mfa.error = null
    },
    setMfaVerificationGrant(state, { payload }) {
      state.mfa.grantFingerprint = payload.fingerprint
      state.mfa.verificationGrant = payload.grant
    },
    clearMfaVerificationGrant(state) {
      state.mfa.grantFingerprint = null
      state.mfa.verificationGrant = null
    }
  },
  extraReducers: (builder) => {
    builder.addCase(startSubmission, (state) => {
      state.isSubmitting = true
    })
    builder.addCase(finishSubmission, (state) => {
      state.isSubmitting = false
    })
    builder.addCase('inviteFlow/create/pending', (state) => {
      state.isNextStepLoading = true
    })
    builder.addCase('inviteFlow/create/fulfilled', (state) => {
      state.isNextStepLoading = false
      state.mfa.error = null
      state.mfa.grantFingerprint = null
      state.mfa.open = false
      state.mfa.verificationGrant = null
      state.recaptcha.showChallenge = false
      state.recaptcha.v2Token = null
    })
    builder.addCase('inviteFlow/create/rejected', (state) => {
      state.isNextStepLoading = false
    })
    builder.addCase(validate.pending, (state, { meta }) => {
      if (meta.arg?.nextStep) {
        state.isNextStepLoading = true
      } else {
        state.isPreviousStepLoading = true
      }
    })
    builder.addCase(validate.fulfilled, (state, { meta, payload }) => {
      state.form = payload
      if (meta.arg?.nextStep) {
        state.isNextStepLoading = false
      } else {
        state.isPreviousStepLoading = false
      }
    })
    builder.addCase(validate.rejected, (state, { meta }) => {
      if (meta.arg?.nextStep) {
        state.isNextStepLoading = false
      } else {
        state.isPreviousStepLoading = false
      }
    })
    builder.addCase(createUserGroup.pending, (state) => {
      state.userGroups.isLoading = true
    })
    builder.addCase(createUserGroup.fulfilled, (state, { payload }) => {
      state.userGroups.isLoading = false

      state.userGroups.list = [
        ...state.userGroups.list,
        payload
      ]
    })
    builder.addCase(createUserGroup.rejected, (state) => {
      state.userGroups.isLoading = false
    })
    builder.addCase(selectUserGroup, (state, { payload }) => {
      state.managedEventTypes = initialState.managedEventTypes
      state.userGroups.selected = payload
        ? state.userGroups.list.find((group) => group.id === payload)
        : null
    })
    builder.addCase(fetchUserGroups.pending, (state) => {
      state.userGroups.isLoading = true
    })
    builder.addCase(fetchUserGroups.fulfilled, (state, { payload }) => {
      if (payload.collection.length) {
        state.userGroups.list = payload.collection
      } else {
        state.userGroups.list = []
      }

      state.userGroups.isLoading = false
    })
    builder.addCase(fetchUserGroups.rejected, (state) => {
      state.userGroups.isLoading = false
    })
    builder.addCase(nextStep, (state) => {
      if (state.currentStepIndex + 1 < state.steps.length) {
        state.currentStepIndex += 1
      }
    })
    builder.addCase(previousStep, (state) => {
      if (state.currentStepIndex > 0) {
        state.currentStepIndex -= 1
      }
    })
    builder.addCase(moveToStep, (state, { payload }) => {
      state.currentStepIndex = payload
    })
    builder.addCase(fetchManagedEventTypes.pending, (state) => {
      state.managedEventTypes.isLoading = true
    })
    builder.addCase(fetchManagedEventTypes.fulfilled, (state, action) => {
      state.managedEventTypes.isLoading = false
      state.managedEventTypes.list = action.payload.collection
      state.managedEventTypes.pagination.currentPage = action.payload.pagination.current_page
      state.managedEventTypes.pagination.pageSize = action.payload.pagination.page_size
      state.managedEventTypes.pagination.totalCount = action.payload.pagination.total_count
    })
    builder.addCase(fetchManagedEventTypes.rejected, (state) => {
      state.managedEventTypes.isLoading = false
    })
    builder.addCase(resetFormInvitations, (state) => {
      state.form.invitations = []
    })
    builder.addCase(saveBackendErrors, (state, { payload: { type, payload } }) => {
      if (type !== ERROR_TYPES.validation) return

      state.errors = translateBackendErrorsToForm(payload)
    })
  }
})

const collectResponseValues = (response, targetKey) => {
  const values = []
  const seen = new WeakSet()

  const visit = (value, depth = 0) => {
    if (!value || typeof value !== 'object' || depth > 4 || seen.has(value)) return

    seen.add(value)
    Object.getOwnPropertyNames(value).forEach((key) => {
      let nestedValue
      try {
        nestedValue = value[key]
      } catch {
        return
      }

      if (key === targetKey) values.push(nestedValue)
      if (nestedValue && typeof nestedValue === 'object') visit(nestedValue, depth + 1)
    })
  }

  visit(response)
  return values
}

const normalizeResponseText = (value) => String(value).trim().toLowerCase()
  .replace(/[.!?]+$/, '')

const findResponseObjectByType = (response, targetType) => {
  const seen = new WeakSet()
  const normalizedTargetType = normalizeResponseText(targetType)

  const visit = (value, depth = 0) => {
    if (!value || typeof value !== 'object' || depth > 4 || seen.has(value)) return null

    seen.add(value)

    let type
    try {
      type = value.type
    } catch {
      type = null
    }

    if (typeof type === 'string' && normalizeResponseText(type) === normalizedTargetType) {
      return value
    }

    for (const key of Object.getOwnPropertyNames(value)) {
      let nestedValue
      try {
        nestedValue = value[key]
      } catch {
        nestedValue = null
      }

      const match = visit(nestedValue, depth + 1)
      if (match) return match
    }

    return null
  }

  return visit(response)
}

const hasResponseType = (response, type) => collectResponseValues(response, 'type')
  .some((value) => normalizeResponseText(value) === type)

const findResponseString = (response, key) => collectResponseValues(response, key)
  .find((value) => typeof value === 'string' && value.length > 0)

const isRecaptchaV2Required = (response) => collectResponseValues(response, 'recaptcha_v2_required')
  .some((value) => value === true || value === 'true')

const isRecaptchaVerificationFailure = (response) => {
  const translatedMessage = normalizeResponseText(I18n.t('errors.messages.recaptcha_error_alt'))
  const knownMessages = new Set([translatedMessage, 'errors.messages.recaptcha_error_alt'])
  const responseMessages = [
    ...collectResponseValues(response, 'message'),
    ...collectResponseValues(response, 'error'),
  ].filter((value) => typeof value === 'string').map(normalizeResponseText)
  const responseTypes = collectResponseValues(response, 'type').map(normalizeResponseText)

  return collectResponseValues(response, 'recaptcha_verification_failed')
    .some((value) => value === true || value === 'true')
    || responseMessages.some((value) => knownMessages.has(value))
    || responseTypes.some((value) => [
      'recaptcha_failed',
      'recaptcha_verification_failed',
      'recaptcha_verification_failure'
    ].includes(value))
}

const mfaGrantFingerprint = ({ eventTypeIds = [], groupId = null, invitations = [] }) => JSON.stringify({
  eventTypeIds: [...eventTypeIds].map(String).sort(),
  groupId,
  invitations: invitations
    .map(({ email, role, userGroupId, userGroupRole }) => ({
      email: email?.trim().toLowerCase(),
      role: role || null,
      userGroupId: userGroupId || null,
      userGroupRole: userGroupRole || null,
    }))
    .sort((first, second) => JSON.stringify(first).localeCompare(JSON.stringify(second)))
})

const mfaErrorMessage = (response) => (
  findResponseString(response, 'message')
  || I18n.t('publisher.account_settings.login.unlink_verification_code_invalid')
)

const throwCreateValidationError = ({ dispatch, response, stepId }) => {
  const validationResponse = findResponseObjectByType(response, ERROR_TYPES.validation)
  if (!validationResponse) return

  if (stepId >= 0) dispatch(moveToStep(stepId))
  dispatch(saveBackendErrors(validationResponse))
  setFormBulkErrors(dispatch, validationResponse)
}

const create = createAsyncThunk(
  'inviteFlow/create',
  async (data, { rejectWithValue, dispatch, getState }) => {
    const fingerprint = mfaGrantFingerprint(data)
    const { grantFingerprint, verificationGrant } = getState().inviteFlow.mfa || {}
    const grantMatchesPayload = verificationGrant && grantFingerprint === fingerprint

    if (verificationGrant && !grantMatchesPayload) {
      dispatch(slice.actions.clearMfaVerificationGrant())
    }

    const [success, response] = await createInvitations({
      ...data,
      mfaVerificationGrant: grantMatchesPayload ? verificationGrant : null
    })

    const returnedGrant = findResponseString(response, 'mfa_verification_grant')
    const mfaAuthenticationFailed = hasResponseType(response, 'mfa_required')
      || hasResponseType(response, 'mfa_failed')

    if (returnedGrant && !mfaAuthenticationFailed) {
      dispatch(slice.actions.setMfaVerificationGrant({ fingerprint, grant: returnedGrant }))
      dispatch(slice.actions.closeMfa())
    }

    if (hasResponseType(response, 'race_condition')) {
      dispatch(showError(I18n.t('publisher.billing.failure.please_try_again')))
      return rejectWithValue(response)
    }

    if (isSubscriptionQuantityError(response)) {
      showSubscriptionQuantityError({
        dispatch,
        onSuccess: () => dispatch(validate({ data, stepId: 1 })),
        response,
        stepId: 1
      })
      return rejectWithValue(response)
    }

    if (!success) {
      if (hasResponseType(response, 'mfa_required')) {
        dispatch(slice.actions.clearMfaVerificationGrant())
        dispatch(slice.actions.openMfa())
      } else if (hasResponseType(response, 'mfa_failed')) {
        dispatch(slice.actions.setMfaError(mfaErrorMessage(response)))
      } else if (isRecaptchaV2Required(response)) {
        dispatch(slice.actions.closeMfa())
        dispatch(slice.actions.showRecaptchaChallenge())
      } else if (isRecaptchaVerificationFailure(response)) {
        dispatch(slice.actions.closeMfa())
        dispatch(slice.actions.refreshRecaptchaChallenge())
        dispatch(showError(I18n.t('errors.messages.recaptcha_error_alt')))
      } else if (!findResponseObjectByType(response, ERROR_TYPES.validation)) {
        dispatch(showHttpError(response))
      }
      return rejectWithValue(response)
    }

    const isSingleOrganization = isSingleSelector(getState())
    if (isSingleOrganization) {
      dispatch(rootApi.util.invalidateTags(['CURRENT_ORGANIZATION']))
    }

    dispatch(fetchStatistics())
    return response
  }
)

const executeRecaptcha = async (dispatch, state) => {
  try {
    const callback = recaptchaCallbackSelector(state)
    const token = typeof callback === 'function' ? await callback() : null
    return { token }
  } catch {
    dispatch(showError(I18n.t('errors.messages.recaptcha_error_alt')))
    return { failed: true, token: null }
  }
}

export const validateAndCreate = (data) => async (dispatch, getState) => withSubmissionLock({
  dispatch,
  getState,
  callback: async () => {
    const { payload, error } = await dispatch(validate({ data, stepId: 1 }))

    if (error) {
      await dispatch(saveBackendErrors(payload))
      setFormBulkErrors(dispatch, payload)
      return false
    }

    const { failed: recaptchaFailed, token: recaptchaToken } = await executeRecaptcha(dispatch, getState())
    if (recaptchaFailed) return false

    const recaptchaV2Token = recaptchaV2TokenSelector(getState())

    const { payload: createPayload, error: createError } = await dispatch(create({
      ...data,
      recaptchaToken,
      recaptchaV2Token
    }))

    if (createError) {
      throwCreateValidationError({ dispatch, response: createPayload, stepId: 1 })
      return false
    }

    if (createPayload && createPayload.type === 'race_condition') { return false }

    changeSet.clear('new-invitations-wizard')
    dispatch(nextStep())
    return true
  }
})

export const initialize = () => async (dispatch, getState) => {
  const isAdminOrGroupAdmin = isAdminOrOwnerOrGroupAdminSelector(getState())

  if (!isAdminOrGroupAdmin) {
    const steps = STEPS.filter((stepName) => stepName !== 'assign_event_types')
    return dispatch(slice.actions.setSteps(steps))
  }

  await Promise.all([
    dispatch(fetchManagedEventTypes()),
    dispatch(fetchUserGroups())
  ])

  const state = getState()

  const managedEventTypes = state.inviteFlow.managedEventTypes.list
  const anyGroups = state.inviteFlow.userGroups.list.length > 0
  const currentUserIsGroupAdminOnly = isOnlyGroupAdminSelector(state)

  const steps = (managedEventTypes.length > 0 || currentUserIsGroupAdminOnly)
    ? STEPS
    : STEPS.filter((stepName) => stepName !== 'assign_event_types')

  dispatch(slice.actions.setAnyGroups(anyGroups))
  return dispatch(slice.actions.setSteps(steps))
}

export const sortManagedEvents = () => async (dispatch) => {
  dispatch(slice.actions.toggleSort())
  dispatch(slice.actions.setPage(1))
  dispatch(fetchManagedEventTypes())
}

export const fetchPage = (page) => (dispatch) => {
  dispatch(slice.actions.setPage(page))
  dispatch(fetchManagedEventTypes())
}

export const { reducer, actions: {
  removeBackendError,
  refreshRecaptchaChallenge,
  setRecaptchaCallback,
  setRecaptchaV2Token,
  setSteps,
  setRole,
  setModalSuccessState,
  setAnyGroups,
  openAddUserModal,
  closeAddUserModal,
  closeMfa,
  clearMfaError,
  clearMfaVerificationGrant,
} } = slice

export const validateAndCreateFromModal = (data) => async (dispatch, getState) => withSubmissionLock({
  dispatch,
  getState,
  callback: async () => {
    const { payload, error } = await dispatch(validate({ data, stepId: 1 }))

    if (error) {
      await dispatch(saveBackendErrors(payload))
      setFormBulkErrors(dispatch, payload)
      return false
    }

    const { failed: recaptchaFailed, token: recaptchaToken } = await executeRecaptcha(dispatch, getState())
    if (recaptchaFailed) return false

    const recaptchaV2Token = recaptchaV2TokenSelector(getState())

    const { payload: createPayload, error: createError } = await dispatch(create({
      ...data,
      recaptchaToken,
      recaptchaV2Token,
      modal: true
    }))

    if (createError) {
      throwCreateValidationError({ dispatch, response: createPayload, stepId: -1 })
      return false
    }

    if (createPayload && createPayload.type === 'race_condition') { return false }

    if (data.invitations.length === 1) {
      dispatch(showSuccess(I18n.t('publisher.add_users_modal.invite_sent')))
    } else {
      dispatch(showSuccess(I18n.t('publisher.add_users_modal.invites_sent', { count: data.invitations.length })))
    }

    await dispatch(resetFormInvitations())
    dispatch(slice.actions.closeMfa())
    dispatch(setModalSuccessState(true))

    return true
  }
})

export const resetPageState = () => async (dispatch) => {
  dispatch(dismissAlert())
  dispatch(slice.actions.resetVerificationState())
}
